import {
    Box,
    Button,
    createListCollection,
    Field,
    Flex,
    Grid,
    GridItem,
    Image,
    Portal,
    Select,
    Separator,
    Slider,
    Stack,
    Text,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import CurrencyInput from "react-currency-input-field";

import { Tooltip } from "@/components/ui/tooltip";
import { usePostLoanSimulationMutation } from "@/hooks/microservicesApi";

const CreditSimulator = ({
    dataDevelopment,
    isGreaterThanLg,
    formatPrice,
    session,
    onOpen,
    setFormOrigin,
}) => {
    const router = useRouter();
    const [postLoanSimulation, { isLoading }] = usePostLoanSimulationMutation();
    const [percentage, setPercentage] = useState(60);
    const [price, setPrice] = useState(
        dataDevelopment?.max_price ||
            dataDevelopment?.operation_props?.presale?.max_price ||
            dataDevelopment?.operation_props?.sale?.max_price ||
            dataDevelopment?.operation_props?.lease?.max_price ||
            1000000
    );
    const [term, setTerm] = useState(5);
    const [showTooltip, setShowTooltip] = useState(false);
    const [shouldSave, setShouldSave] = useState(false);

    //Valores para obtener el calculo
    const [downPayment, setDownPayment] = useState(null);
    const [mensualPayment, setMensualPayment] = useState(null);
    let interestRate = 11.5;

    const termOptions = createListCollection({
        items: [
            { value: 5, label: "5 años" },
            { value: 10, label: "10 años" },
            { value: 15, label: "15 años" },
            { value: 20, label: "20 años" },
            { value: 25, label: "25 años" },
        ],
    });

    useEffect(() => {
        // Solo ejecutar cuando shouldSave es true y mensualPayment ha sido actualizado
        if (shouldSave && mensualPayment > 0) {
            saveLoanSimulation();
            setShouldSave(false); // Resetear para futuros cálculos
        }
    }, [mensualPayment, shouldSave]);

    const handleClic = (annualRate, years, loan) => {
        const mensualRate = annualRate / 100 / 12;
        const paysNum = years * 12;

        // Fórmula equivalente a PAGO(tasa, nper, va)
        const mensualPay =
            (loan * mensualRate * Math.pow(1 + mensualRate, paysNum)) /
            (Math.pow(1 + mensualRate, paysNum) - 1);

        setMensualPayment(mensualPay);
        setShouldSave(true); // Activar el guardado después de actualizar el estado
    };

    //Función para registar los valores calculados en la BD
    const saveLoanSimulation = async () => {
        try {
            await postLoanSimulation({
                development_id: dataDevelopment?.development_id || null,
                contact_id: session?.contact_id || null,
                property_price: price,
                loan_percentage: percentage,
                loan_term_years: term,
                down_payment: downPayment,
                monthly_payment: mensualPayment,
                fixed_interest_rate: interestRate,
            }).unwrap();
        } catch (error) {
            //Log en consola para identificar problema rapidamente
            console.error("Error al enviar la simulación:", error);
        }
    };

    return (
        <Box pt={5}>
            <Flex align="center" gap={6} mb={4}>
                <Image
                    src="https://media.grupoguia.mx/assets/guialink/logo_guialink.png"
                    alt="guialink"
                    maxW="100px"
                />
                <Text color="#002B49" fontSize="sm">
                    Simula tu crédito
                </Text>
            </Flex>
            <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }} gap={4}>
                <GridItem colSpan={2}>
                    <Box
                        borderWidth={1}
                        borderColor="#9D9D9D"
                        borderRadius="md"
                        alignContent="center"
                        justifyItems="center"
                        bg="#F4F4F4"
                        mt={3}
                        px={5}
                        py={5}
                        gap={6}
                        minH="100%"
                    >
                        <Box mb={8} w="100%">
                            <Field.Root
                                variant="floating"
                                id="price"
                                css={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    "& > label": {
                                        transform: `scale(0.85) translateY(-5px)`,
                                        color: "#000",
                                        fontSize: isGreaterThanLg ? "xs" : "2xs",
                                        _focus: {
                                            color: "#3182ce",
                                        },
                                    },
                                }}
                            >
                                <CurrencyInput
                                    id="price"
                                    name="price"
                                    placeholder=""
                                    defaultValue={price}
                                    decimalsLimit={2}
                                    prefix="$"
                                    decimalSeparator="."
                                    onValueChange={(value) => setPrice(Number(value))}
                                    style={{
                                        width: "70%",
                                        padding: "8px 16px",
                                        borderRadius: "9999px",
                                        backgroundColor: "#FFF",
                                        border: "1px solid #E2E8F0",
                                        outline: "none",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#3182ce";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#E2E8F0";
                                    }}
                                />
                                <Field.Label
                                    color="#000"
                                    htmlFor="price"
                                    width="auto"
                                    bg="transparent"
                                    textAlign="center"
                                    justifySelf="anchor-center"
                                >
                                    ¿Cuánto cuesta la propiedad que quieres comprar?
                                </Field.Label>
                            </Field.Root>
                        </Box>

                        <Flex
                            justifyContent="center"
                            align="center"
                            mb={2}
                            gap={5}
                            maxW="90%"
                            mx="auto"
                        >
                            <Image
                                src="https://media.grupoguia.mx/assets/apps-icons/piggy_bank.svg"
                                alt="Piggy Bank"
                                w="40px"
                            />
                            <Box w="100%">
                                <Text fontSize="2xs" mb={2}>
                                    ¿Qué porcentaje de crédito necesitas?*
                                </Text>
                                <Slider.Root
                                    value={[percentage]}
                                    min={40}
                                    max={90}
                                    step={1}
                                    onValueChange={(details) => {
                                        setShowTooltip(true);
                                        setPercentage(details?.value[0]);
                                    }}
                                    onValueChangeEnd={() => setShowTooltip(false)}
                                    flex="1"
                                >
                                    <Slider.Control>
                                        <Slider.Track bg="#B1B1B1">
                                            <Slider.Range bg="#2B6790" />
                                        </Slider.Track>
                                        <Tooltip
                                            content={`${percentage}%`}
                                            open={showTooltip}
                                            showArrow
                                            positioning={{ placement: "bottom" }}
                                            variant="white"
                                        >
                                            <Slider.Thumb cursor="pointer">
                                                <Slider.DraggingIndicator
                                                    layerStyle="fill.solid"
                                                    top="6px"
                                                    rounded="sm"
                                                />
                                            </Slider.Thumb>
                                        </Tooltip>
                                    </Slider.Control>
                                </Slider.Root>
                            </Box>
                            <Image
                                src="https://media.grupoguia.mx/assets/apps-icons/house_cash.svg"
                                alt="House"
                                w="70px"
                            />
                        </Flex>
                        <Text textAlign="center" fontSize="xs" color="#3E3E3E" mt={8} mb="4vh">
                            El monto de tu solicitud de crédito debe ser, como mínimo, el 40% y,
                            como máximo, el 90%.
                        </Text>
                        <Box maxW="70%" minW="50%" mx="auto">
                            <Field.Root
                                id="term"
                                variant="floating"
                                css={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    "& > label": {
                                        transform: "scale(0.85) translateY(-5px)",
                                        color: "#000",
                                        fontSize: "xs",
                                        background: "transparent",
                                    },
                                }}
                            >
                                <Select.Root
                                    collection={termOptions}
                                    rounded="full"
                                    bg="white"
                                    borderWidth={1}
                                    borderColor="#D9D9D9"
                                    fontSize="sm"
                                    color="#4F4F4FEB"
                                    boxShadow="sm"
                                    value={[term] || [5]}
                                    onValueChange={(details) => {
                                        setTerm(Number(details.value[0]));
                                    }}
                                >
                                    <Select.HiddenSelect />
                                    <Select.Control>
                                        <Select.Trigger
                                            cursor="pointer"
                                            rounded="full"
                                            bg="white"
                                            borderWidth={1}
                                            borderColor="#D9D9D9"
                                            fontSize="sm"
                                            color="#4F4F4FEB"
                                            boxShadow="sm"
                                            pl={2}
                                        >
                                            <Select.ValueText placeholder="Selecciona el plazo" />
                                        </Select.Trigger>
                                        <Select.IndicatorGroup pr={2}>
                                            <Select.Indicator />
                                        </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Portal>
                                        <Select.Positioner>
                                            <Select.Content p={2}>
                                                {termOptions.items.map((option) => (
                                                    <Select.Item
                                                        cursor="pointer"
                                                        item={option}
                                                        key={option.value}
                                                        color="gray.600"
                                                        p={1}
                                                    >
                                                        {option.label}
                                                        <Select.ItemIndicator />
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Portal>
                                </Select.Root>
                                <Field.Label color="#000" htmlFor="term">
                                    Selecciona el plazo
                                </Field.Label>
                            </Field.Root>
                        </Box>
                        <Button
                            mt="2vh"
                            w="100%"
                            p={3}
                            borderRadius="sm"
                            bg="#FFFFFF"
                            textAlign="center"
                            borderWidth={1}
                            borderColor="#002B49"
                            fontWeight={400}
                            loading={isLoading}
                            _hover={{ background: "#F0F0F0" }}
                            onClick={(e) => {
                                e.preventDefault();
                                const newDownPayment = price * (1 - percentage / 100);
                                setDownPayment(newDownPayment);
                                handleClic(interestRate, term, price - newDownPayment);
                            }}
                            color="accent.500"
                        >
                            <Text color="#002B49">Calcular</Text>
                        </Button>
                    </Box>
                </GridItem>
                <GridItem colSpan={{ base: 2, md: 1 }} p={{ base: 5, md: 0 }}>
                    <Box
                        borderRadius="md"
                        bgGradient="to-t"
                        gradientFrom="#5D8BD6"
                        gradientTo="#A2C6FA"
                        mt={3}
                        p={5}
                        gap={6}
                        minH="100%"
                    >
                        <Text fontWeight="bold" color="white" textAlign="center">
                            Para adquirir esta <br />
                            propiedad necesitas
                        </Text>
                        <Stack mt={5}>
                            <Box bg="#002B4917" p={2} textAlign="center" color="white">
                                <Text fontSize="2xs">Enganche</Text>
                                <Text fontWeight="bold" fontSize="lg">
                                    {downPayment ? formatPrice(downPayment) : "-"}
                                </Text>
                            </Box>
                            <Box bg="#002B4917" p={1} textAlign="center" color="white">
                                <Text fontSize="2xs">Pago mensual hipotecario</Text>
                                <Text fontWeight="bold" fontSize="lg">
                                    {mensualPayment
                                        ? formatPrice(Math.ceil(mensualPayment * 100) / 100)
                                        : "-"}
                                </Text>
                            </Box>
                            <Box bg="#002B4917" p={1} textAlign="center" color="white">
                                <Text fontSize="2xs">Tasa fija:</Text>
                                <Text fontWeight="bold" fontSize="lg">
                                    {interestRate} %
                                </Text>
                            </Box>
                        </Stack>
                        <Button
                            fontSize="sm"
                            color="#000"
                            bg="#FECB2E"
                            fontWeight={400}
                            w="100%"
                            mt={5}
                            _hover={{ bg: "#E0A800" }}
                            borderRadius="sm"
                            p={4}
                            onClick={(e) => {
                                e.preventDefault();
                                window.open("/guialink/", "_blank");
                            }}
                        >
                            Obtener mi precalificación
                        </Button>
                        <Flex gap={2} w="110%" mt={4}>
                            <Image
                                src="https://media.grupoguia.mx/assets/apps-icons/orientation.svg"
                                alt="orientation_icon"
                                maxW="20px"
                                objectFit="contain"
                            />
                            <Text
                                target="_blank"
                                rel="noopener noreferrer"
                                cursor="pointer"
                                color="white"
                                fontSize="xs"
                                _hover={{ textDecoration: "underline" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onOpen();
                                    setFormOrigin("adviser_contact");
                                }}
                            >
                                Quiero recibir orientación de un asesor
                            </Text>
                        </Flex>
                    </Box>
                </GridItem>
            </Grid>
            <Text mt="5vh" fontSize="2xs">
                Tasa de interés anual y fija de 8.9% a 38.9% sin IVA, sujeta a evaluación. Sujeto a
                aprobación del banco en el que solicites el crédito.
                <br />
                CAT PROMEDIO: 12.1 a 14.4% sin IVA. Dependiendo del tipo de vivienda y porcentaje de
                financiamiento sin IVA. Fecha de cálculo:
                {" " +
                    new Intl.DateTimeFormat("es-ES", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    }).format(new Date())}
            </Text>
            <Text mt="3vh" fontSize="2xs">
                *La presente cotización es de carácter informativo y no constituye una oferta
                vinculante ni compromiso de aprobación del crédito. Las condiciones finales están
                sujetas a la aprobación del banco o entidad financiera correspondiente. Grupo Guía
                no es una institución financiera y actúa únicamente como intermediario comercial.
            </Text>
            {dataDevelopment?.boomerang_props?.selection_type && isGreaterThanLg && (
                <Button
                    mt="5vh"
                    bg="#FFFFFF"
                    w="100%"
                    textAlign="center"
                    borderWidth={1}
                    borderColor="#002B49"
                    fontWeight={400}
                    _hover={{ background: "#F0F0F0" }}
                    borderRadius="sm"
                    onClick={(e) => {
                        e.preventDefault();
                        router.push(
                            `${router.asPath}/seleccion?code=${dataDevelopment?.development_id}`
                        );
                    }}
                >
                    <Text color="accent.500">Consulta unidades disponibles</Text>
                </Button>
            )}
            <Separator borderColor="#B1B1B1" mt="7vh" />
        </Box>
    );
};

export default CreditSimulator;
